import MuiAccordion from "@mui/material/Accordion";
import { styled } from "@mui/material/styles";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import classNames from "classnames";
import { graphql, navigate, useStaticQuery } from "gatsby";
import React, { useEffect, useMemo, useState } from "react";
import { useCompanyInfo, useFinanceSummary, useIsTablet } from "../../hooks";
import { useSelectedJointAccountDetails } from "../../hooks/joint-account.hooks";
import { useMyInfo } from "../../hooks/user.hooks.ts";
import { primaryColor } from "../../scss/colors.module.scss";
import {
  Account,
  Dashboard,
  Finance,
  Logout,
  Property,
  Support,
  Transactions,
} from "../../svgs";
import { logout } from "../../utils/auth";
import MobileMenu from "./components/mobile-menu";
import SwitchAccount from "./components/switch-account";
import MenuLeftItemWValue from "./menuLeftItem";
import "./menu_left.scss";
import MenuLeftItem from "./menu_left_item";
import { getFullName } from "../../utils/user-data-util";
import { BUSINESS_TYPE } from "../../commons/enum";

const PREFIX = "index";

const classes = {
  root: `${PREFIX}-root`,
  expanded: `${PREFIX}-expanded`,
  root2: `${PREFIX}-root2`,
  content: `${PREFIX}-content`,
  expanded2: `${PREFIX}-expanded2`,
  root3: `${PREFIX}-root3`,
};

const Root = styled("div")(() => ({
  [`& .${classes.root}`]: {
    backgroundColor: "transparent",
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    color: "var(--surfaceColor)",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
  },

  [`& .${classes.expanded}`]: {
    "&:first-of-type": {
      marginBottom: 0,
    },
  },

  [`& .${classes.root2}`]: {
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    padding: 0,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },

  [`& .${classes.content}`]: {
    "& svg": {
      transform: "rotate(0deg)",
      transition: "transform .2s ease-in-out",
    },
    margin: 0,
    "&$expanded": {
      margin: 0,
      "& svg": {
        transform: "rotate(180deg)",
      },
    },
  },

  [`& .${classes.expanded2}`]: {},

  [`& .${classes.content}.MuiAccordionSummary-content`]: {
    margin: 0,
  },

  [`& .${classes.root3}`]: {
    padding: 0,
    backgroundColor: "var(--surfaceColor)",
    "& a.ml-items-body": {
      "&:hover": {
        backgroundColor: "var(--surfaceColor)",
      },
      padding: "1rem",
      color: "var(--darkTextColor)",
    },
  },
}));

const Accordion = MuiAccordion;

const AccordionSummary = MuiAccordionSummary;

const AccordionDetails = MuiAccordionDetails;

const sideBarMenuKey = {
  Dashboard: "Dashboard",
  Transactions: "Transactions",
  Finance: "Finance",
  Property: "Property",
  MyBroker: "My Broker",
  Account: "Account",
};

const sideBarMenuItem = [
  {
    icon: <Dashboard />,
    title: "Dashboard",
    href: "/",
    key: sideBarMenuKey.Dashboard,
  },
  {
    icon: <Transactions />,
    title: "Money",
    href: "/transactions",
    key: sideBarMenuKey.Transactions,
  },
  {
    icon: <Finance />,
    title: "Lending",
    href: "/finance",
    key: sideBarMenuKey.Finance,
  },
  {
    icon: <Property />,
    title: "Property",
    href: "/property",
    key: sideBarMenuKey.Property,
  },
  {
    icon: <Support />,
    title: "My Broker",
    href: "/support",
    key: sideBarMenuKey.MyBroker,
  },
  {
    icon: <Account />,
    title: "Account",
    href: "/account",
    key: sideBarMenuKey.Account,
  },
];

const MenuLeft = (props) => {
  const data = useStaticQuery(graphql`
    query {
      gitCommit(latest: { eq: true }) {
        hash
        date
      }
      gitTag(latest: { eq: true }) {
        name
      }
      gitBranch(current: { eq: true }) {
        name
      }
    }
  `);
  const { gitBranch, gitCommit: { date: gitDate = "" } = {} } = data;
  const gitTagName = gitBranch?.name || "";
  const summary = useFinanceSummary() || {};
  const { data: userInfo } = useMyInfo();
  const { data: companyInfo } = useCompanyInfo();
  const isTablet = useIsTablet();
  const [logoutExpanded, setLogoutExpanded] = useState(false);
  const selectedJointAcocuntDetails = useSelectedJointAccountDetails();
  const accountName = useMemo(() => {
    return selectedJointAcocuntDetails
      ? selectedJointAcocuntDetails.name
      : getFullName(userInfo);
  }, [userInfo, selectedJointAcocuntDetails]);
  const [checkedItem, setcheckedItem] = useState("");
  const [sidebarCollapse, setSidebarCollapse] = useState(true);

  const renderSideBarMenuItem = useMemo(() => {
    return sideBarMenuItem.map((sideBarItem) => {
      if (
        sideBarItem.key === sideBarMenuKey.MyBroker &&
        companyInfo?.businessType === BUSINESS_TYPE.PLANNER
      ) {
        // eslint-disable-next-line no-param-reassign
        sideBarItem.title = "My Advisor";
        return sideBarItem;
      }
      return sideBarItem;
    });
  }, [companyInfo]);

  const {
    netWealth = 0,
    income = 0,
    expenses = 0,
    borrowCapacity = 0,
    cashDeposit = 0,
    propertyPortfolio = 0,
    liabilities = 0,
  } = summary;

  const netCashFlow = useMemo(() => {
    return income - expenses;
  }, [income, expenses]);

  const { location: { pathname = "" } = {} } = props;

  useEffect(() => {
    switch (true) {
      case pathname.indexOf("transactions") === 1:
        setcheckedItem(sideBarMenuKey.Transactions);
        break;
      case pathname.indexOf("finance") === 1:
        setcheckedItem(sideBarMenuKey.Finance);
        break;
      case pathname.indexOf("property") === 1:
        setcheckedItem(sideBarMenuKey.Property);
        break;
      case pathname.indexOf("support") === 1:
        setcheckedItem(sideBarMenuKey.MyBroker);
        break;
      case pathname.indexOf("account") === 1:
        setcheckedItem(sideBarMenuKey.Account);
        break;
      default:
        setcheckedItem(sideBarMenuKey.Dashboard);
        break;
    }
  }, [pathname]);

  useEffect(() => {
    setLogoutExpanded(false);
  }, [isTablet]);

  const handleLogoutExpanded = () => {
    setLogoutExpanded(!logoutExpanded);
  };
  const handleToggleSidebar = (status) => {
    if (status) {
      setLogoutExpanded(false);
    }
    setSidebarCollapse(status);
  };
  const handleLogout = () => {
    logout();
  };

  return (
    <Root className="menu_left">
      <div
        className={classNames("desktop-menu", {
          collapsed: sidebarCollapse,
        })}
        onMouseLeave={() => handleToggleSidebar(true)}
        onMouseEnter={() => handleToggleSidebar(false)}
      >
        <div className="desktop-menu-container">
          <Accordion
            className="position-relative"
            square
            expanded={logoutExpanded}
            onChange={!sidebarCollapse ? handleLogoutExpanded : undefined}
            classes={{
              root: classes.root,
              expanded: classes.expanded,
            }}
          >
            <AccordionSummary
              aria-controls="panel3d-content"
              id="panel3d-header"
              classes={{
                root: classes.root2,
                content: classes.content,
                expanded: classes.expanded2,
              }}
            >
              <div className="ml-avatar-wraper">
                <div className="ml-avatar-name">
                  {sidebarCollapse ? accountName.match(/\b(\w)/g) : accountName}
                </div>
                <ExpandMoreIcon />
              </div>
            </AccordionSummary>
            <AccordionDetails
              className="logout-button-group"
              classes={{
                root: classes.root3,
              }}
            >
              <MenuLeftItem
                icon={<Logout fillcolor={primaryColor} borderColor="var(--darkTextColor)" />}
                title="Logout"
                checked={false}
                onClick={handleLogout}
              />
              <SwitchAccount />
              <div className="code-version">
                <p className="m-0">
                  Last Updated On: {new Date(gitDate).toLocaleString() || " "}
                </p>
              </div>
            </AccordionDetails>
          </Accordion>
          <div className="ml-transaction-container">
            <MenuLeftItemWValue
              name="Net Cashflow"
              value={netCashFlow}
              className="net-wealth"
              isNetItem
              info="It's and average of the last three months to give a per month estimate"
            />
            <MenuLeftItemWValue name="Money in" value={income} />
            <MenuLeftItemWValue name="Money out" value={expenses} />
            <MenuLeftItemWValue
              name="Borrowing Capacity"
              value={borrowCapacity}
            />
          </div>
          <div className="ml-transaction-container">
            <MenuLeftItemWValue
              name="Net Capital"
              value={netWealth}
              className="net-wealth"
              isNetItem
            />
            <MenuLeftItemWValue name="Cash Balance" value={cashDeposit} />
            <MenuLeftItemWValue
              name="Property Portfolio"
              value={propertyPortfolio}
            />
            <MenuLeftItemWValue name="Liabilities" value={liabilities} />
          </div>
          <div className="ml-items-group">
            <div className="ml-items-container">
              {renderSideBarMenuItem.map((sideBarItem) => (
                <MenuLeftItem
                  key={sideBarItem.key}
                  icon={sideBarItem.icon}
                  title={sideBarItem.title}
                  checked={checkedItem === sideBarItem.key}
                  onClick={() => navigate(sideBarItem.href)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {isTablet && (
        <MobileMenu
          sideBarMenuItem={renderSideBarMenuItem}
          name={accountName}
          handleLogout={handleLogout}
          checkedItem={checkedItem}
          isExpanded={logoutExpanded}
          handleIsExpanded={handleLogoutExpanded}
          data={{ income, cashDeposit, expenses }}
          gitVersion={{ gitTagName, gitDate }}
        />
      )}
    </Root>
  );
};

export default React.memo(MenuLeft);
